import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import FormattedText from './FormattedText';
import { SERVICES } from '../data';
import { fetchServicePreviews } from '../lib/contentful/services/services';
import './Services.css';

export default function Services() {
  const localIconById = useMemo(
    () =>
      SERVICES.reduce((acc, service) => {
        acc[service.id] = service.icon;
        return acc;
      }, {}),
    []
  );
  const localIconByTitle = useMemo(
    () =>
      SERVICES.reduce((acc, service) => {
        acc[service.title?.toLowerCase?.() || ''] = service.icon;
        return acc;
      }, {}),
    []
  );
  const fallbackPreviews = useMemo(
    () =>
      SERVICES.map(service => ({
        id: service.id,
        icon: service.icon,
        title: service.title,
        summary: service.summary,
      })),
    []
  );
  const [services, setServices] = useState(fallbackPreviews);

  useEffect(() => {
    let isActive = true;

    async function loadServicePreviews() {
      try {
        const previews = await fetchServicePreviews();
        if (!isActive || !previews.length) return;
        const withIconFallbacks = previews.map((preview, index) => ({
          ...preview,
          icon:
            preview.icon ||
            localIconById[preview.id] ||
            localIconByTitle[preview.title?.toLowerCase?.() || ''] ||
            fallbackPreviews[index]?.icon ||
            '◈',
        }));
        setServices(withIconFallbacks);
      } catch {
        // Keep local fallback content if Contentful is unavailable.
      }
    }

    loadServicePreviews();

    return () => {
      isActive = false;
    };
  }, [fallbackPreviews, localIconById, localIconByTitle]);

  return (
    <section className="services" id="services">
      <div className="container">
        <div className="services__header">
          <span className="section-eyebrow">What We Do</span>
          <h2 className="section-title">Services</h2>
          <p className="services__intro">
            Specialized expertise across the full martech spectrum — from strategy and architecture through to implementation and measurement.
          </p>
        </div>

        <div className="services__grid">
          {services.map((service, i) => (
            <div key={service.id} className="service-card" style={{ animationDelay: `${i * 0.1}s` }}>
              <div className="service-card__icon">{service.icon || '◈'}</div>
              <h3 className="service-card__title">{service.title}</h3>
              <FormattedText as="p" className="service-card__summary" text={service.summary} />
              <Link to={`/services/${service.id}`} className="service-card__link">
                Learn More
                <span className="service-card__arrow">→</span>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
